import { Tooltip } from '@mantine/core';
import { MessageSquarePlus } from 'lucide-react';

import IconButton from '@/components/IconButton.tsx';

import { COMMUNICATION_UNAVAILABLE_TEXT } from '@/constants/communication.ts';
import { FeatureFlag } from '@/constants/feature-flags.ts';

import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { TaskModalMode } from '@/models/TaskModalMode.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { getInstanceId } from '@/utils/appointmentUtils.ts';
import { buildAppointmentCommunicationContext } from '@/utils/communication-utils.ts';
import { openCommunicationModal } from '@/utils/modal/communication-modal-utils.tsx';
import { getPatientDisplayName } from '@/utils/patientUtils.ts';
import { buildAppointmentTaskPrefill, withCommunicationTaskFields } from '@/utils/task-utils.ts';
import { openBookingTaskModal } from '@/utils/task-utils.tsx';

interface Props {
    appointment: DailyAppointment;
}

/**
 * Writes a COMMUNICATION activity into the patient's Insuite case folder for this appointment. Deliberately
 * rendered for every appointment status — cancellations and reschedules are among the main things the contact
 * center records.
 */
export const AppointmentCommunicationAction = ({ appointment }: Props) => {
    const instanceId = getInstanceId(appointment) ?? '';
    const { data: practitionerData } = useEnrichedPractitionerDataForSpecificInstance(instanceId);
    const { value: isCommunicationVisible } = useFeatureFlag(FeatureFlag.IS_COMMUNICATION_ENABLED);

    if (practitionerData?.pvs_provider !== PVSProvider.DOC_CIRRUS || !isCommunicationVisible) return undefined;

    const patientName = getPatientDisplayName(appointment) ?? '';
    // Undefined when the appointment has no calendar or no insurance type yet (both are required by the
    // endpoint). Disabled with an explanation beats a button that fails once an entry has been typed.
    const context = buildAppointmentCommunicationContext(appointment, patientName);

    const handleClick = (): void => {
        if (!context) return;
        openCommunicationModal({
            context: context,
            onSavedWithTask: (content) =>
                openBookingTaskModal({
                    mode: TaskModalMode.EDITOR,
                    prefill: withCommunicationTaskFields(
                        buildAppointmentTaskPrefill(appointment, practitionerData, patientName),
                        content
                    ),
                }),
        });
    };

    return (
        <div className="flex flex-1 justify-center">
            <Tooltip
                label={COMMUNICATION_UNAVAILABLE_TEXT}
                disabled={!!context}
                multiline
                classNames={{ tooltip: 'max-w-md' }}
            >
                {/* span wrapper so the tooltip still shows while the button itself is disabled. */}
                <span>
                    <IconButton
                        icon={MessageSquarePlus}
                        text="Kommunikation"
                        disabled={!context}
                        onClick={handleClick}
                    />
                </span>
            </Tooltip>
        </div>
    );
};
